'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchCountries, uploadFile } from '@eliadmin/core/src/api/general/general-api';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, boolean as __eliadmin_zod_3 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';

import { Heading } from '../ui/heading';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Button } from '../ui/button';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { RiAccountCircleLine } from 'react-icons/ri';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { Field } from '@eliadmin/core/src/components/form/field';
import { MdDomain, MdOutlineEmail } from 'react-icons/md';
import { CgProfile } from 'react-icons/cg';
import { Separator } from '@eliadmin/core/src/components/ui/separator';

type ProfileEditFormValues = z.infer<ReturnType<typeof profileEditSchema>>;

export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);

  const [file, setFile] = useState<File | undefined>(undefined);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { data: countries = { data: [] } } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema(formValidationMessages)),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      gender: customer?.gender ?? undefined,
      address: customer?.address ?? '',
      street_number: customer?.street_number ?? '',
      company_buy: customer?.company_buy ?? false,
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      country: customer?.country ?? undefined,
      iban: customer?.iban ?? '',
      swift: customer?.swift ?? '',
      address_delivery: customer?.address_delivery ?? '',
      street_number_delivery: customer?.street_number_delivery ?? '',
      postcode_delivery: customer?.postcode_delivery ?? '',
      city_delivery: customer?.city_delivery ?? '',
      country_delivery: customer?.country_delivery ?? undefined,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
      // Defaults to true so delivery fields are hidden by default
      same_billing_address: customer?.same_billing_address ?? true,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await uploadFile({
          authToken,
          formData,
          locale,
        });
        data.image = res.data;
      } catch (e) {
        //TODO: handle error / create custom field for this error
        return;
      }
    }

    try {
      await mutateAsync({
        locale,
        data: data as any,
        authToken,
      }).then(res => {
        const typedRes = res as { data: Customer };
        typedRes &&
          initCustomerStore({
            authToken,
            customer: { ...typedRes?.data },
          });
        return typedRes;
      });
      toast({
        variant: 'success',
        title: t('text4071'),
      });
      router.refresh();
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  const fullName = `${customer.name} ${customer.surname}`;

  const sameBilling = form.watch('same_billing_address');

  return (
    <Form {...form}>
      <form
        className="mx-auto flex w-full max-w-[688px] flex-col items-center justify-center gap-6 sm:gap-8 md:gap-10 lg:gap-[50px]"
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-4 rounded-[24px] border p-6',
            'ea-profile-edit-form__profile-header',
          )}
        >
          <CgProfile size={48} className="text-foreground" />
          <div className="flex w-full flex-col items-center justify-center gap-3">
            <Heading title={fullName} center tag="h4" />
            <div className="flex w-full items-center justify-center gap-2">
              <MdOutlineEmail size={24} className="text-[#117F11]" />
              <Paragraph
                size="base"
                className="text-center font-bold text-[#117F11]"
                color="primary"
              >
                {customer.email}
              </Paragraph>
            </div>
          </div>
        </div>
        <div className="grid w-full grid-cols-12 gap-4 md:gap-6">
          <FormField
            control={form.control}
            name="company_buy"
            render={({ field }) => (
              <FormItem
                className={cn('ea-register-form__company-type-field', 'col-span-12 w-full')}
              >
                <FormControl>
                  <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
                    <div
                      onClick={() => field.onChange(false)}
                      className={cn(
                        'col-span-1 flex h-[52px] w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[12px] p-4 text-sm font-medium tracking-tight text-foreground sm:h-12 sm:p-3 sm:text-base',
                        field.value === false ? 'border-2 border-primary' : 'border border-border',
                      )}
                    >
                      <RiAccountCircleLine size={24} />
                      <p>{t('text2234')}</p>
                    </div>

                    <div
                      onClick={() => field.onChange(true)}
                      className={cn(
                        'col-span-1 flex h-[52px] w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[12px] p-4 text-sm font-medium tracking-tight text-foreground sm:h-12 sm:p-3 sm:text-base',
                        field.value === true ? 'border-2 border-primary' : 'border border-border',
                      )}
                    >
                      <MdDomain size={24} />
                      <p>{t('text1432')}</p>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid w-full grid-cols-12 gap-4 md:gap-6">
          <SectionHeader title={t('text360')} />
          <Field
            required
            control={form.control}
            name="phone"
            label={t('text36')}
            placeholder={t('text36')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            type="tel"
          />
          <Field
            control={form.control}
            name="email"
            type="email"
            label="E-mail"
            placeholder="E-mail"
            isDisabled={true}
            className={{ wrapper: 'col-span-12 w-full opacity-50 sm:col-span-6' }}
            before={<MdOutlineEmail size={20} className="text-[#9CA3AF]" />}
          />
        </div>
        <Separator />

        {/* Contact fields */}

        <div className="grid w-full grid-cols-12 gap-4 md:gap-6">
          <SectionHeader title={t('text198')} />
          {form.watch('company_buy') === true ? (
            <CompanyDataFields form={form} countries={countries.data} />
          ) : (
            <PersonalDataFields form={form} countries={countries.data} />
          )}
          <FormField
            control={form.control}
            name="same_billing_address"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'col-span-12 flex w-full flex-row items-center justify-start gap-2',
                  'ea-register-form__same-billing-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal sm:text-base">{t('text3705')}</FormLabel>
              </FormItem>
            )}
          />
        </div>
        {!sameBilling && (
          <div className="grid w-full grid-cols-12 gap-x-6 gap-y-4">
            <DeliveryDataFields form={form} />
          </div>
        )}
        <div className="col-span-12 flex w-full items-center justify-center">
          <Button
            type="submit"
            className="w-full bg-[#00A500] text-white hover:bg-[#00A500]/90 md:w-auto"
            isLoading={form.formState.isSubmitting}
          >
            {t('text1952')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Heading
      title={title}
      tag="h3"
      className={{ container: 'col-span-12 w-full items-start justify-start text-start' }}
    />
  );
}

function PersonalDataFields({ form, countries }: { form: any; countries: Country[] }) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        control={form.control}
        name="name"
        label={t('text33')}
        placeholder={t('text33')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="surname"
        label={t('text34')}
        placeholder={t('text34')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="address"
        label={t('text157')}
        placeholder={t('text157')}
        className={{ wrapper: 'col-span-8 w-full' }}
      />
      <Field
        control={form.control}
        name="street_number"
        label={t('text485')}
        placeholder={t('text485')}
        className={{ wrapper: 'col-span-4 w-full' }}
      />
      <Field
        control={form.control}
        name="postcode"
        label={t('text40')}
        placeholder={t('text40')}
        className={{ wrapper: 'col-span-4 w-full' }}
      />
      <Field
        control={form.control}
        name="city"
        label={t('text39')}
        placeholder={t('text39')}
        className={{ wrapper: 'col-span-8 w-full' }}
      />
      <Field
        control={form.control}
        name="country.id"
        label={t('text519')}
        placeholder={t('text519')}
        type="select"
        options={countries.map(c => ({ label: c.name, value: c.id }))}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
    </>
  );
}

function CompanyDataFields({ form, countries }: { form: any; countries: Country[] }) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        control={form.control}
        name="name"
        label={t('text3706')}
        placeholder={t('text3706')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="surname"
        label={t('text3707')}
        placeholder={t('text3707')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="company"
        label={t('text1188')}
        placeholder={t('text1188')}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        control={form.control}
        name="ico"
        label={t('text42')}
        placeholder={t('text42')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="dic"
        label={t('text43')}
        placeholder={t('text43')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="ic_dph"
        label={t('text44')}
        placeholder={t('text44')}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        control={form.control}
        name="address_company"
        label={t('text157')}
        placeholder={t('text157')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        control={form.control}
        name="street_number_company"
        label={t('text485')}
        placeholder={t('text485')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="postcode_company"
        label={t('text40')}
        placeholder={t('text40')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="city_company"
        label={t('text39')}
        placeholder={t('text39')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        control={form.control}
        name="country.id"
        label={t('text519')}
        placeholder={t('text519')}
        type="select"
        options={countries.map(c => ({ label: c.name, value: c.id }))}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
    </>
  );
}

function DeliveryDataFields({ form }: { form: any }) {
  const { t } = useTranslations();

  return (
    <>
      <SectionHeader title={t('text32')} />
      <Field
        control={form.control}
        name="name_delivery"
        placeholder={t('text33')}
        label={t('text33')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="surname_delivery"
        placeholder={t('text34')}
        label={t('text34')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="address_delivery"
        placeholder={t('text157')}
        label={t('text157')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        control={form.control}
        name="street_number_delivery"
        placeholder={t('text485')}
        label={t('text485')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="postcode_delivery"
        placeholder={t('text40')}
        label={t('text40')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="city_delivery"
        placeholder={t('text39')}
        label={t('text39')}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export const profileEditSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: __eliadmin_zod_1().optional(),
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    username: __eliadmin_zod_1().optional(),
    address: __eliadmin_zod_1().optional(),
    street_number: __eliadmin_zod_1().optional(),
    postcode: __eliadmin_zod_1().optional(),
    city: __eliadmin_zod_1().optional(),
    country: __eliadmin_zod_0({
        id: __eliadmin_zod_1().or(__eliadmin_zod_2()).optional(),
      })
      .optional(),
    company_buy: __eliadmin_zod_3().optional(),
    same_billing_address: __eliadmin_zod_3().optional(),
    company: __eliadmin_zod_1().optional(),
    ico: __eliadmin_zod_1().optional(),
    dic: __eliadmin_zod_1().optional(),
    ic_dph: __eliadmin_zod_1().optional(),
    gender: __eliadmin_zod_0({
        id: __eliadmin_zod_1().or(__eliadmin_zod_2()).optional(),
      })
      .optional(),
    image: __eliadmin_zod_0({
        id: __eliadmin_zod_2(),
      })
      .optional(),
    address_temporary: __eliadmin_zod_1().optional(),
    street_number_temporary: __eliadmin_zod_1().optional(),
    postcode_temporary: __eliadmin_zod_1().optional(),
    city_temporary: __eliadmin_zod_1().optional(),
    country_temporary: __eliadmin_zod_0({
        id: __eliadmin_zod_1(),
      })
      .optional(),
    email_company: __eliadmin_zod_1().email().optional(),
    address_company: __eliadmin_zod_1().optional(),
    street_number_company: __eliadmin_zod_1().optional(),
    postcode_company: __eliadmin_zod_1().optional(),
    city_company: __eliadmin_zod_1().optional(),
    country_company: __eliadmin_zod_0({
        id: __eliadmin_zod_1(),
      })
      .optional(),
    address_delivery: __eliadmin_zod_1().optional(),
    street_number_delivery: __eliadmin_zod_1().optional(),
    postcode_delivery: __eliadmin_zod_1().optional(),
    city_delivery: __eliadmin_zod_1().optional(),
    country_delivery: __eliadmin_zod_0({
        id: __eliadmin_zod_1(),
      })
      .optional(),
    name_delivery: __eliadmin_zod_1().optional(),
    surname_delivery: __eliadmin_zod_1().optional(),
    company_delivery: __eliadmin_zod_1().optional(),
    iban: __eliadmin_zod_1().optional(),
    swift: __eliadmin_zod_1().optional(),
    instagram: __eliadmin_zod_1().optional(),
    facebook: __eliadmin_zod_1().optional(),
    national_id_number: __eliadmin_zod_1().optional(),
  });
